import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit'

import type { AirportCountrySummary } from '~/helpers/airport-directory'
import { searchAirportDirectory } from '~/services/api/handlers'
import type { AirportDirectoryAirport } from '~/types/airport-directory'

export type AirportDirectorySearchParams = {
    country?: string
    query: string
}

export type AirportDirectorySearchPayload = {
    airports: AirportDirectoryAirport[]
    query: string
}

export const searchAirports = createAsyncThunk<AirportDirectorySearchPayload, AirportDirectorySearchParams>(
    'airports/searchAirports',
    async ({ query, country = '' }: AirportDirectorySearchParams, { signal }) => {
        const response = await searchAirportDirectory(query, country, signal)
        return {
            airports: Array.isArray(response.data) ? response.data : [],
            query,
        }
    },
)

export type AirportDirectorySearchStatus = 'idle' | 'loading' | 'succeeded' | 'failed'

export type AirportsState = {
    countries: AirportCountrySummary[]
    country: AirportCountrySummary | null
    defaultAirports: AirportDirectoryAirport[]
    searchedAirports: AirportDirectoryAirport[]
    isLoading: boolean
    searchError: string
    searchQuery: string
    searchRequestId: string
    searchStatus: AirportDirectorySearchStatus
    page: number
    totalAirports: number
    totalPages: number
}

export const initialState: AirportsState = {
    countries: [],
    country: null,
    defaultAirports: [],
    searchedAirports: [],
    isLoading: false,
    searchError: '',
    searchQuery: '',
    searchRequestId: '',
    searchStatus: 'idle',
    page: 1,
    totalAirports: 0,
    totalPages: 1,
}

export const airportsSlice = createSlice({
    name: 'airports',
    initialState,
    reducers: {
        setDefaultAirports: (state, action: PayloadAction<AirportDirectoryAirport[]>) => {
            state.defaultAirports = action.payload
        },
        clearSearchedAirports: (state) => {
            state.searchedAirports = []
            state.isLoading = false
            state.searchError = ''
            state.searchQuery = ''
            state.searchRequestId = ''
            state.searchStatus = 'idle'
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(searchAirports.pending, (state, action) => {
                state.isLoading = true
                state.searchedAirports = []
                state.searchError = ''
                state.searchQuery = action.meta.arg.query
                state.searchRequestId = action.meta.requestId
                state.searchStatus = 'loading'
            })
            .addCase(searchAirports.fulfilled, (state, action) => {
                if (state.searchRequestId !== action.meta.requestId) return

                state.searchedAirports = action.payload.airports
                state.isLoading = false
                state.searchQuery = action.payload.query
                state.searchStatus = 'succeeded'
            })
            .addCase(searchAirports.rejected, (state, action) => {
                if (state.searchRequestId !== action.meta.requestId) return

                state.searchedAirports = []
                state.isLoading = false
                state.searchError = action.meta.aborted ? '' : 'Airport search is temporarily unavailable.'
                state.searchStatus = action.meta.aborted ? 'idle' : 'failed'
            })
    },
})

export const { setDefaultAirports, clearSearchedAirports } = airportsSlice.actions

export default airportsSlice.reducer

type AirportsRootState = { airports: AirportsState }

export const defaultAirportsSelector = (state: AirportsRootState) => state.airports.defaultAirports
export const searchedAirportsSelector = (state: AirportsRootState) => state.airports.searchedAirports
export const isLoadingSelector = (state: AirportsRootState) => state.airports.isLoading
export const searchErrorSelector = (state: AirportsRootState) => state.airports.searchError || ''
export const searchQuerySelector = (state: AirportsRootState) => state.airports.searchQuery || ''
export const searchStatusSelector = (state: AirportsRootState) => state.airports.searchStatus || 'idle'
export const airportDirectoryPageSelector = (state: AirportsRootState) => state.airports.page
export const airportDirectoryTotalAirportsSelector = (state: AirportsRootState) => state.airports.totalAirports
export const airportDirectoryTotalPagesSelector = (state: AirportsRootState) => state.airports.totalPages
export const airportDirectoryCountriesSelector = (state: AirportsRootState) => state.airports.countries
export const airportDirectoryCountrySelector = (state: AirportsRootState) => state.airports.country
